// UhrzeitFeld – Uhrzeit wie im Google Kalender: Feld zeigt „11:00“ (24 Stunden), Klick öffnet eine Liste in
// 15-Minuten-Schritten (aktuelle Zeit markiert und in die Mitte gescrollt), Eintippen geht auch („930“, „9:30“, „9“ → 09:30).
// Mit `ab` (Ende-Felder): Liste beginnt beim Start und zeigt die Dauer dahinter („11:30 (30 Min.)“, „12:00 (1 Std.)“).
// Ein Baustein für alle Stellen mit Uhrzeit (Termin, Einsatz, Arbeitszeit). Werte immer „HH:MM“ oder „“.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const z2 = (n) => String(n).padStart(2, "0");
const inMin = (w) => { const m = /^(\d{1,2}):(\d{2})$/.exec(w || ""); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const alsText = (min) => `${z2(Math.floor(min / 60))}:${z2(min % 60)}`;

/** Eingabe -> „HH:MM“ oder null. Versteht „930“, „9:30“, „9.30“, „09:30“, „9“, „1430“. */
export const leseUhrzeit = (eingabe) => {
  const t = String(eingabe || "").trim().replace(/[.,\s]/g, ":").replace(/uhr$/i, "");
  let h, m;
  let r;
  if ((r = /^(\d{1,2}):(\d{1,2})$/.exec(t))) { h = Number(r[1]); m = Number(r[2]); }
  else if ((r = /^(\d{1,2})$/.exec(t))) { h = Number(r[1]); m = 0; }
  else if ((r = /^(\d{1,2})(\d{2})$/.exec(t))) { h = Number(r[1]); m = Number(r[2]); }
  else return null;
  if (h > 23 || m > 59) return null;
  return `${z2(h)}:${z2(m)}`;
};

/** „2026-10-28T16:00“ <-> Datum + Uhrzeit (für Felder mit getrenntem Datum und Uhrzeit) */
export const teileDatumZeit = (s) => { const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(s || ""); return { datum: m ? m[1] : "", zeit: m && m[2] ? m[2] : "" }; };
export const fuegeDatumZeit = (datum, zeit) => (datum ? `${datum}T${zeit || "00:00"}` : "");

const dauerText = (min) => {
  if (min < 60) return `${min} Min.`;
  if (min === 60) return "1 Std.";
  const h = min / 60;
  return `${String(Number.isInteger(h) ? h : h.toFixed(2).replace(/0$/, "").replace(".", ",")).replace(".", ",")} Std.`;
};

export default function UhrzeitFeld({ value = "", onChange, ab = null, testId = "uhrzeit", className = "", placeholder = "00:00", ariaLabel = "Uhrzeit" }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState(value || "");
  const [pos, setPos] = useState(null);
  const feld = useRef(null);
  const liste = useRef(null);
  useEffect(() => { setText(value || ""); }, [value]);

  const start = ab ? (inMin(ab) ?? 0) : 0;
  const eintraege = [];
  for (let m = start; m < 24 * 60; m += 15) eintraege.push(m);
  const jetzt = new Date();
  const jetztMin = Math.floor((jetzt.getHours() * 60 + jetzt.getMinutes()) / 15) * 15;
  const gewaehlt = inMin(value);

  const oeffnen = () => {
    if (offen) return;
    const r = feld.current.getBoundingClientRect();
    const platzUnten = window.innerHeight - r.bottom;
    const hoch = Math.min(280, Math.max(160, platzUnten < 280 ? r.top - 8 : platzUnten - 8));
    const oben = platzUnten < 280 && r.top > platzUnten;
    setPos({ left: Math.max(4, Math.min(r.left, window.innerWidth - Math.max(r.width, 170) - 4)), width: Math.max(r.width, 170), top: oben ? r.top - hoch - 2 : r.bottom + 2, hoch });
    setOffen(true);
  };
  const schliessen = () => setOffen(false);

  // Beim Öffnen: gewählte (sonst aktuelle) Zeit markieren und in die Mitte scrollen
  useLayoutEffect(() => {
    if (!offen || !liste.current) return;
    const ziel = liste.current.querySelector("[data-ziel]");
    if (ziel) liste.current.scrollTop = Math.max(0, ziel.offsetTop - liste.current.clientHeight / 2 + ziel.offsetHeight / 2);
  }, [offen]);
  useEffect(() => {
    if (!offen) return undefined;
    const aussen = (e) => { if (!liste.current?.contains(e.target) && !feld.current?.contains(e.target)) schliessen(); };
    const scroll = (e) => { if (!liste.current?.contains(e.target)) schliessen(); };
    document.addEventListener("pointerdown", aussen);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", schliessen);
    return () => { document.removeEventListener("pointerdown", aussen); window.removeEventListener("scroll", scroll, true); window.removeEventListener("resize", schliessen); };
  }, [offen]);

  const uebernehmen = (w) => { setText(w); onChange?.(w); schliessen(); };
  const fertigTippen = () => {
    if (!text.trim()) { if (value) onChange?.(""); return; }
    const w = leseUhrzeit(text);
    if (w) { setText(w); if (w !== value) onChange?.(w); } else setText(value || "");
  };
  const zielMin = gewaehlt !== null && eintraege.includes(gewaehlt) ? gewaehlt : (gewaehlt === null && eintraege.includes(jetztMin) ? jetztMin : (gewaehlt !== null ? eintraege.find((m) => m >= gewaehlt) : undefined));

  return (
    <div className="relative" ref={feld}>
      <input
        type="text" inputMode="numeric" autoComplete="off" aria-label={ariaLabel} placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={(e) => { oeffnen(); e.target.select(); }}
        onClick={oeffnen}
        onBlur={fertigTippen}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); fertigTippen(); schliessen(); }
          else if (e.key === "Escape") { schliessen(); }
        }}
        className={`w-full border rounded-sm p-2 text-sm tabular-nums ${className}`}
        data-testid={testId}
      />
      {offen && pos && createPortal(
        <div ref={liste} role="listbox" data-testid={`${testId}-liste`}
          className="fixed z-[10050] overflow-y-auto bg-background border rounded-md shadow-xl py-1"
          style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.hoch }}>
          {eintraege.map((m) => {
            const t = alsText(m);
            const istGewaehlt = m === gewaehlt;
            const istJetzt = m === jetztMin;
            return (
              <button key={m} type="button" role="option" aria-selected={istGewaehlt}
                data-testid={`${testId}-opt-${t}`} data-jetzt={istJetzt ? "ja" : undefined} {...(m === zielMin ? { "data-ziel": "1" } : {})}
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => uebernehmen(t)}
                className={`w-full h-10 px-3 flex items-center justify-between text-sm text-left tabular-nums hover:bg-muted ${istGewaehlt ? "bg-primary text-primary-foreground hover:bg-primary" : ""} ${istJetzt && !istGewaehlt ? "font-semibold ring-1 ring-inset ring-primary/60" : ""}`}>
                <span>{t}{ab && m > start ? " " : null}{ab && m > start ? <span className={`ml-1 ${istGewaehlt ? "" : "text-muted-foreground"}`}>({dauerText(m - start)})</span> : null}</span>
                {istJetzt && <span className={`text-[10px] ${istGewaehlt ? "" : "text-primary"}`}>jetzt</span>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
